import { useId, type ReactElement } from 'react';

import clsx from 'clsx';
import { defaults } from 'lodash-es';
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from 'react-hook-form';

import { Button } from '@quick/components/Button';
import { Password, WrappedInput } from '@quick/components/Forms';
import { useJSLoaded } from '@quick/utils';

interface LoginForm {
  mobile_phone: string;
  password: string;
}

type splitString = [string, string];
const defaultCopies = {
  signIn: ['Sign in with', 'your account'] as splitString,
  login: 'Log In',
  newCustomer: ['Are you a', 'New Customer?'] as splitString,
  description:
    'By creating an account in Quick, you can track your services in real time',
};

interface NewLoginProps {
  className?: string;
  onValid: SubmitHandler<LoginForm>;
  onInvalid?: SubmitErrorHandler<LoginForm>;
  recover: ReactElement;
  signUp: ReactElement;
  copies?: Partial<typeof defaultCopies>;
}

export function NewLogin({
  className,
  onValid,
  onInvalid,
  recover,
  signUp,
  copies,
}: NewLoginProps) {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<LoginForm>();
  const t = defaults(copies, defaultCopies);
  const onSubmit = handleSubmit(onValid, onInvalid);
  const loaded = useJSLoaded();

  const id = useId();

  return (
    <form
      className={clsx(
        'grid grid-cols-1 rounded-xl sm:grid-cols-[3fr,2fr]',
        className
      )}
      onSubmit={onSubmit}
    >
      <div
        className="flex flex-col gap-4 rounded-tl-xl rounded-tr-xl bg-white p-10 text-gray-500
          sm:rounded-bl-xl sm:rounded-tr-none dark:bg-gray-800 dark:text-gray-200"
      >
        <p className="text-center text-2xl">
          <span className="block font-semibold">{t.signIn[0]}</span>
          <span className="block font-bold">{t.signIn[1]}</span>
        </p>
        <WrappedInput
          {...register('mobile_phone', { required: 'Requerido' })}
          label="E-MAIL"
          labelSize="sm"
          placeholder="mail@quick.us"
          autoFocus
          id={`${id}-mail`}
        />
        <Password
          {...register('password', { required: 'Requerido', minLength: 8 })}
          placeholder="Password"
          aria-label="Password"
          autoComplete="off"
          id={`${id}-password`}
        />
        <div className="flex justify-between">
          <div />
          {/* TODO: Here goes the `Remember me` button for when we have that functionality */}
          <span className="text-sm">{recover}</span>
        </div>
        <Button
          type="submit"
          className="self-center rounded-full px-8"
          disabled={!loaded || isSubmitting}
        >
          {t.login}
        </Button>
      </div>
      <div
        className="bg-brand flex max-w-xs flex-col justify-end gap-8 rounded-bl-xl rounded-br-xl
          p-10 text-gray-600 sm:w-min sm:rounded-bl-none sm:rounded-tr-xl"
      >
        <p className="text-left text-2xl">
          <span className="block font-semibold">{t.newCustomer[0]}</span>
          <span className="block whitespace-nowrap font-bold">
            {t.newCustomer[1]}
          </span>
        </p>
        <p className="leading-5">{t.description}</p>
        <span className="self-center">{signUp}</span>
      </div>
    </form>
  );
}
